import { Star, Quote } from "lucide-react";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { useTestimonials } from "@/hooks/useTestimonials";

export function TestimonialsSection() {
  const { data: testimonials } = useTestimonials();
  
  // Use first 3 valid testimonials or fallback if empty (though static generation ensures data)
  const displayTestimonials = testimonials?.slice(0, 3) || [];

  if (displayTestimonials.length === 0) return null;

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
          <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-4">
            Patient Stories
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4">
            Hear From Our Patients
          </h2>
          <p className="text-foreground/70 text-lg">
            Real stories from international patients who trusted TrustedHeal for their healthcare journey.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {displayTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-card rounded-lg p-6 lg:p-8 shadow-soft relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 text-primary/10">
                <Quote className="w-12 h-12" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-foreground/80 mb-6 italic leading-relaxed">
                "{testimonial.quote}"
              </p>

              {/* Treatment Tag */}
              {testimonial.treatment && (
                <div className="inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full mb-6">
                  {testimonial.treatment}
                </div>
              )}

              {/* Author */}
              <div className="flex items-center gap-4">
                {testimonial.image ? (
                  <img
                    src={getOptimizedImageUrl(testimonial.image, 100, 100)}
                    alt={testimonial.name}
                    width={100}
                    height={100}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                   <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                      {testimonial.name.charAt(0)}
                   </div>
                )}
                <div>
                  <div className="font-semibold text-secondary">{testimonial.name}</div>
                  <div className="text-sm text-muted-foreground">{testimonial.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

